import { FC } from "react";
import { Form, Input, Row, Segmented, Typography } from "../../../design-system";
import { CompanyImage } from "../../../common/components/CompanyImage";
import { logoDevUrlForWebsite } from "../../../common/LogoDev";
import classes from "./VendorLogoField.module.less";

/**
 * Which logo URL gets saved. "default" is the logo.dev lookup derived from the
 * website; "custom" is whatever the user typed. Both values are kept while the
 * form is open, so toggling back and forth loses nothing.
 */
export type LogoMode = "default" | "custom";

export const FIELD_MAX = 255;

export const clean = (value?: string): string | undefined => {
    const trimmed = value?.trim();
    return trimmed ? trimmed : undefined;
};

/** The logo URL a form in `mode` would save. */
export const logoUrlFor = (mode: LogoMode, website?: string, customLogo?: string) =>
    mode === "default" ? logoDevUrlForWebsite(website) : clean(customLogo);

/** The mode a saved vendor is in: custom unless its logo is the one its website derives. */
export const logoModeOf = (hero?: string, website?: string): LogoMode =>
    hero && hero !== logoDevUrlForWebsite(website) ? "custom" : "default";

export interface VendorLogoFieldProps {
    /** For the preview's initials fallback. */
    vendorName: string;
    /** The form's current website value, which the default mode derives the logo from. */
    website?: string;
    /** The form's current `customLogo` value. */
    customLogo?: string;
    mode: LogoMode;
    onModeChange: (mode: LogoMode) => void;
}

/**
 * The "Logo image" section of the vendor create and edit forms: a
 * default/custom toggle, the custom URL field (a `customLogo` item of the
 * surrounding Form) and a preview of whichever logo would be saved.
 */
export const VendorLogoField: FC<VendorLogoFieldProps> = ({
    vendorName,
    website,
    customLogo,
    mode,
    onModeChange,
}) => {
    const activeLogo = logoUrlFor(mode, website, customLogo);
    return (
        <div className={classes.logoSection}>
            <Row justify="space-between" align="middle" className={classes.logoHeader}>
                <Typography.Text>Logo image</Typography.Text>
                <Segmented
                    size="small"
                    value={mode}
                    onChange={(value) => onModeChange(value as LogoMode)}
                    options={[
                        { label: "Default", value: "default" },
                        { label: "Custom", value: "custom" },
                    ]}
                />
            </Row>
            {mode === "custom" && (
                <Form.Item
                    name="customLogo"
                    rules={[
                        {
                            max: FIELD_MAX,
                            message: `Keep the logo URL under ${FIELD_MAX} characters`,
                        },
                    ]}
                >
                    <Input aria-label="Custom logo URL" placeholder="https://..." autoFocus />
                </Form.Item>
            )}
            <Form.Item style={{ marginBottom: 0 }}>
                {activeLogo ? (
                    <div data-testid="vendor-logo-preview">
                        {/* Keyed on the URL so a failed load resets when the URL changes. */}
                        <CompanyImage
                            key={activeLogo}
                            logo={activeLogo}
                            name={vendorName}
                            size="large"
                        />
                    </div>
                ) : (
                    <div className={classes.previewPlaceholder} data-testid="vendor-logo-placeholder">
                        <Typography.Text type="secondary">
                            {mode === "default"
                                ? "Enter a website above to preview its logo"
                                : "Enter a logo image URL above to preview it"}
                        </Typography.Text>
                    </div>
                )}
            </Form.Item>
        </div>
    );
};
